/* ============================================================================
   ERROR BOUNDARY — contains a render/runtime crash in any screen so a single
   bad screen shows a recoverable fallback instead of blanking the whole app.
   (The console bridges a legacy global-script design layer; a boundary here is
   the safety net for the parts TypeScript can't check.)
   ============================================================================ */
import React from 'react';

interface Props {
  children: React.ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface for diagnostics; in production this is the only trace we get.
    console.error('[Yperea] screen crashed:', error, info.componentStack);
  }

  private handleReload = (): void => {
    this.setState({ error: null });
    window.location.reload();
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isDev = Boolean(import.meta.env.DEV);
    return (
      <div
        className="yp-root"
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          padding: 24,
          textAlign: 'center',
          background: 'var(--bg)',
          color: 'var(--ink)',
        }}
      >
        <span className="yp-h" style={{ fontSize: 20 }}>
          Something went wrong
        </span>
        <span style={{ fontSize: 13, color: 'var(--sub)', maxWidth: 440, lineHeight: 1.5 }}>
          This view hit an unexpected error. Reloading usually clears it. If it keeps
          happening, let the team know what you were doing.
        </span>
        {isDev ? (
          <pre
            style={{
              maxWidth: 'min(90vw,640px)',
              overflow: 'auto',
              textAlign: 'left',
              fontSize: 11,
              color: 'var(--red)',
              background: 'var(--panel-hi)',
              border: '1px solid var(--line)',
              borderRadius: 12,
              padding: 12,
            }}
          >
            {error.message}
          </pre>
        ) : null}
        <button
          className="yp-btn"
          onClick={this.handleReload}
          style={{
            background: 'var(--accent)',
            color: 'var(--on-accent)',
            borderRadius: 12,
            padding: '10px 18px',
            fontFamily: "'Poppins',sans-serif",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
